<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    mdiCalendarClock,
    mdiCalendarMonthOutline,
    mdiCalendarPlus,
    mdiCalendarSync,
    mdiPlus,
    mdiPrinterOutline,
  } from "@mdi/js"
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import type { CalendarDisplayControls } from "../../../../../../../../calendar-api"
  import {
    SCHEDULING_ACTION_TOOLTIP_CLASS,
    schedulingActionTooltips,
  } from "../../../../../../../../../../_shared/_logic/action-tooltips"
  import { filterActiveOrganizationUsers } from "../../../../../../../../_shared/_logic/organization-users"

  type FilterDisplayMode = "compact" | "detailed"

  type Props = {
    calendarControls: CalendarDisplayControls
    title?: string
    organization: OrganizationFrame | null
    allowedEmployeeIds?: string[] | null
    canManageScheduling?: boolean
    createDisabled?: boolean
    onCreateAppointment?: (() => void) | null
    onCreateBlocker?: (() => void) | null
    onManageCalendarIntegrations?: (() => void) | null
    hasCalendarIntegrations?: boolean
    showPrint?: boolean
    filterMode?: FilterDisplayMode | null
    onFilterModeChange?: ((value: FilterDisplayMode) => void) | null
  }

  let {
    calendarControls,
    title = "Terminkalender",
    organization,
    allowedEmployeeIds = null,
    canManageScheduling = false,
    createDisabled = false,
    onCreateAppointment = null,
    onCreateBlocker = null,
    onManageCalendarIntegrations = null,
    hasCalendarIntegrations = false,
    showPrint = false,
    filterMode = null,
    onFilterModeChange = null,
  }: Props = $props()
  const showFilterModeToggle = $derived(Boolean(filterMode && onFilterModeChange))
  const filterModeOptions: { value: FilterDisplayMode; label: string }[] = [
    { value: "compact", label: "Kompakt" },
    { value: "detailed", label: "Ausführlich" },
  ]
  const activeUsers = $derived(filterActiveOrganizationUsers(organization?.users ?? []))

  const canCreate = $derived(
    Boolean(
      (onCreateAppointment || onCreateBlocker) &&
      canManageScheduling &&
      (allowedEmployeeIds ? allowedEmployeeIds.length > 0 : activeUsers.length > 0),
    ),
  )
</script>

<SectionTitle
  {title}
  darkMode={false}
  helpModalTextId="calendar"
  styleOuter="margin-bottom: 1.2rem;"
  helpModalContext={{ planningMode: true, hasCalendarIntegrations }}
>
  {#snippet buttonSnippet()}
    {#if showPrint}
      <CTA
        type="tertiaryButton"
        icon={mdiPrinterOutline}
        content=""
        tooltipContent={schedulingActionTooltips.calendarPrint}
        tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
        darkMode={false}
        onClick={() => window.print()}
      />
    {/if}
    {#if showFilterModeToggle}
      <ToggleCTA
        options={filterModeOptions}
        value={filterMode ?? "compact"}
        darkMode={false}
        onChange={(value) => {
          const nextValue = `${value ?? filterMode ?? "compact"}`.toLowerCase()
          onFilterModeChange?.(nextValue === "detailed" ? "detailed" : "compact")
        }}
      />
    {/if}
    <ToggleCTA
      options={[
        { value: "timeline", label: "Zeitplan", icon: mdiCalendarClock },
        { value: "overview", label: "Übersicht", icon: mdiCalendarMonthOutline },
      ]}
      value={calendarControls.view}
      darkMode={false}
      onChange={(value) =>
        calendarControls.onViewChange(value === "overview" ? "overview" : "timeline")}
    />
    {#if canCreate}
      {#if onCreateBlocker}
        <CTA
          type="primaryButton"
          icon={mdiPlus}
          content="Blocker anlegen"
          tooltipContent="Abwesenheit oder internen Blocker anlegen."
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          darkMode={false}
          disabled={createDisabled}
          onClick={() => onCreateBlocker?.()}
          options={{
            label: "Weitere Kalenderaktionen",
            tooltipClassName: SCHEDULING_ACTION_TOOLTIP_CLASS,
            items: [
              ...(onCreateAppointment
                ? [
                    {
                      id: "create-appointment",
                      label: "Termin anlegen",
                      icon: mdiCalendarPlus,
                      disabled: createDisabled,
                      reason: schedulingActionTooltips.calendarCreateAppointment,
                      tooltipClassName: SCHEDULING_ACTION_TOOLTIP_CLASS,
                      onClick: () => onCreateAppointment?.(),
                    },
                  ]
                : []),
              {
                id: "manage-calendars",
                label: "Kalender verwalten",
                icon: mdiCalendarSync,
                disabled: !onManageCalendarIntegrations,
                reason: "Kalenderprovider, Status und Einrichtung je Mitarbeiter anzeigen.",
                tooltipClassName: SCHEDULING_ACTION_TOOLTIP_CLASS,
                onClick: () => onManageCalendarIntegrations?.(),
              },
            ],
          }}
        />
      {:else if onCreateAppointment}
        <CTA
          type="primaryButton"
          icon={mdiPlus}
          content="Termin anlegen"
          tooltipContent={schedulingActionTooltips.calendarCreateAppointment}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          darkMode={false}
          disabled={createDisabled}
          onClick={() => onCreateAppointment?.()}
        />
      {/if}
    {/if}
    {#if !canCreate || !onCreateBlocker}
      <CTA
        type="secondaryButton"
        icon={mdiCalendarSync}
        content="Kalender verwalten"
        tooltipContent="Kalenderprovider, Status und Einrichtung je Mitarbeiter anzeigen."
        tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
        darkMode={false}
        disabled={!onManageCalendarIntegrations}
        onClick={() => onManageCalendarIntegrations?.()}
      />
    {/if}
  {/snippet}
</SectionTitle>
